import React, { useEffect } from "react";
import { ArcButton, ArcMenuItem, ArcOutlinedInput, ArcOutlinedSelect, ArcStack, ArcText } from "@src/core/ui";

import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { selectFreehandShapeUponCreation, setFreehandShapeUponCreation, setZoneMapLocationUponCreation, useCreateZoneMutation } from "@src/features/zones/redux";
import { routesConfig } from "@src/core/routes";
import { useDispatch, useSelector } from "react-redux";
import { ICreateZoneStage2Form } from "./types";
import { ZoneTypeName } from "@src/features/zones/config/zones-constants";
import { ZoneShapeType, ZoneType } from "@src/core/features";


export const CreateZoneStage2Page: React.FC = () => {

  const defaultValues = {
    zoneName: '',
    zoneDescription: '',
    polygonCoordinates: [],
    zoneType: ZoneType.TEMPORARY,
  } as ICreateZoneStage2Form;

  const { control, setValue, formState: { errors }, handleSubmit } = useForm<ICreateZoneStage2Form>({ defaultValues });

  const navigate = useNavigate();
  const [createZone, { isLoading }] = useCreateZoneMutation();

  const dispatch = useDispatch();
  const freehandShapeUponCreation = useSelector(selectFreehandShapeUponCreation);

  useEffect(() => {
    return () => {
      dispatch(setFreehandShapeUponCreation(null));
    };
  }, []);


  const onCancel =()=> { 
    dispatch(setFreehandShapeUponCreation(null));
    navigate(routesConfig.zones);
  };

  const onClear = () => {
    dispatch(setFreehandShapeUponCreation({
      id: freehandShapeUponCreation?.id || 0,
      name: freehandShapeUponCreation?.name || '',
      type: freehandShapeUponCreation?.type || ZoneType.TEMPORARY,
      polygonCoordinates: [],
    }));
  };

  const onSubmit = async (data: ICreateZoneStage2Form) => {
    try {
      const payload = {
        name: data.zoneName,
        description: data.zoneDescription,
        zoneType: data.zoneType,
        isActive: true,
        polygonCoordinates: freehandShapeUponCreation?.polygonCoordinates || [],
        shape: ZoneShapeType.POLYGON,
      };
      const result = await createZone(payload).unwrap();
      navigate(routesConfig.zones); 
      dispatch(setZoneMapLocationUponCreation(null));
    } catch (error) {
      console.error('Failed to create zone:', error);
    }
  };
    
  return (
    <ArcStack height="100%" direction="column" justifyContent="space-between">
      <ArcStack flex={1}>
        <ArcText.h4 fontWeight={500} marginTop={2}> DRAW ZONE </ArcText.h4>
        <ArcText.h5 sx={{color: 'text.secondary', marginTop: 1}}> Draw a shape on the map to set up a zone. </ArcText.h5>

        <ArcStack flex={1}>
          <ArcStack marginTop={3} spacing={2}>

            <Controller
              control={control}
              name="zoneType"
              render={({ field: { value, onChange } }) => (
                <ArcOutlinedSelect
                  label="Zone Type"
                  value={value}
                  onChange={(e) => {
                    onChange(e);
                    dispatch(setFreehandShapeUponCreation({
                      name: freehandShapeUponCreation?.name || "",
                      type: e.target.value as ZoneType,
                      polygonCoordinates: freehandShapeUponCreation?.polygonCoordinates || [],
                    }));
                  }}
                  error={Boolean(errors.zoneType)}
                  helperText={errors.zoneType?.message}
                >
                  {Object.entries(ZoneTypeName).map(([key, name]) => (
                    <ArcMenuItem key={key} value={key}>
                      {name}
                    </ArcMenuItem>
                  ))}
                </ArcOutlinedSelect>
              )}
            />

            <Controller
              name="zoneName"
              control={control}
              rules={{
                required: "Zone name is required",
                maxLength: { value: 20, message: "Maximum 20 characters allowed" },
              }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <ArcOutlinedInput
                  autoComplete="off"
                  label="Zone Name"
                  value={value || ""}
                  error={Boolean(error)}
                  helperText={error?.message}
                  maxLength={20}
                  onChange={(e) => {
                    const val = e.target.value;
                    onChange(val);
                    setValue("zoneName", val);
                    dispatch(setFreehandShapeUponCreation({
                      name: val,
                      type: freehandShapeUponCreation?.type || ZoneType.TEMPORARY,
                      polygonCoordinates: freehandShapeUponCreation?.polygonCoordinates || [],
                    }));
         
                  }}
                />
              )}
            />

            <Controller
              name="zoneDescription"
              control={control}
              rules={{
                maxLength: { value: 200, message: "Maximum 200 characters allowed" },
              }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <ArcOutlinedInput
                  autoComplete="off"
                  label="Zone Description"
                  value={value || ""}
                  error={Boolean(error)}
                  helperText={error?.message}
                  maxLength={200}
                  multiline
                  onChange={onChange}
                />
              )}
            />
          </ArcStack>
        </ArcStack>
      </ArcStack>

      <ArcStack spacing={2} px={1}>
        <ArcStack direction='row' spacing={2}>
          <ArcButton.ghost
            fullWidth 
            size='large'
            onClick={onCancel}> 
          Cancel 
          </ArcButton.ghost>

          <ArcButton.ghost
            fullWidth 
            size='large'
            onClick={onClear}> 
          Clear Zone
          </ArcButton.ghost>
        </ArcStack>
        <ArcButton.brand 
          fullWidth
          size='large'
          type='submit'
          isLoading={isLoading}
          onClick={handleSubmit(onSubmit)}> 
          Create
        </ArcButton.brand>
      </ArcStack>
 
    </ArcStack>
  );
};
